import { useRef, useState } from "react";
import { useToaster } from "utils";
import { PaziaDeleteModal } from "components/molecules";
import {
  useTrainerFiles,
  useUploadTrainerFile,
  useSetTrainerFileStatus,
  useDeleteTrainerFile,
  useTrainerFileComments,
  useAddTrainerFileCommentAdmin,
} from "api/hooks";
import {
  TrainerFileApi,
  TrainerFileCommentApi,
  TRAINER_FILE_UPLOADED_BY,
  TRAINER_FILE_STATUS,
  TrainerFileStatusApi,
  trainerFilesApi,
} from "api/trainers/trainerFilesApi";
import { TrainerFilePreviewModal } from "./TrainerFilePreviewModal";

const PREVIEWABLE_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

interface TrainerFilesPanelProps {
  trainerId: number;
}

const STATUS_LABEL: Record<TrainerFileStatusApi, string> = {
  [TRAINER_FILE_STATUS.PENDING_REVIEW]: "W weryfikacji",
  [TRAINER_FILE_STATUS.PAID]: "Opłacona",
  [TRAINER_FILE_STATUS.REJECTED]: "Odrzucona",
};

export const TrainerFilesPanel = ({ trainerId }: TrainerFilesPanelProps) => {
  const { showSuccess, showError } = useToaster();
  const { data: files = [] } = useTrainerFiles(trainerId);
  const uploadMutation = useUploadTrainerFile(trainerId);
  const statusMutation = useSetTrainerFileStatus(trainerId);
  const deleteMutation = useDeleteTrainerFile(trainerId);
  const [note, setNote] = useState("");
  const [previewFileId, setPreviewFileId] = useState<number | null>(null);
  const [deleteFileId, setDeleteFileId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    try {
      await uploadMutation.mutateAsync({ file, note: note || undefined });
      setNote("");
      showSuccess("Plik został udostępniony trenerowi");
    } catch {
      showError("Nie udało się wgrać pliku");
    }
  };

  const handleDownload = async (file: TrainerFileApi) => {
    try {
      const response = await trainerFilesApi.downloadFile(trainerId, file.id);
      const url = URL.createObjectURL(response.data as unknown as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originalFileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showError("Nie udało się pobrać pliku");
    }
  };

  const handleSetStatus = async (fileId: number, status: TrainerFileStatusApi) => {
    try {
      await statusMutation.mutateAsync({ fileId, status });
      showSuccess("Status zaktualizowany");
    } catch {
      showError("Nie udało się zmienić statusu");
    }
  };

  const handleDelete = async () => {
    if (deleteFileId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteFileId);
      showSuccess("Plik został usunięty");
    } catch {
      showError("Nie udało się usunąć pliku");
    } finally {
      setDeleteFileId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Notatka (opcjonalnie)"
          className="border rounded px-3 py-2 text-sm w-full sm:flex-1"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="whitespace-nowrap text-sm font-semibold text-white bg-teal-600 rounded px-4 py-2"
        >
          Udostępnij plik
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
            e.target.value = "";
          }}
        />
      </div>

      <div className="flex flex-col gap-3">
        {files.length < 1 && <div className="text-sm text-slate-500">Brak plików.</div>}
        {files.map((file) => (
          <TrainerFileRow
            key={file.id}
            trainerId={trainerId}
            file={file}
            onDownload={() => handleDownload(file)}
            onSetStatus={(status) => handleSetStatus(file.id, status)}
            onPreview={() => setPreviewFileId(file.id)}
            onDelete={() => setDeleteFileId(file.id)}
          />
        ))}
      </div>

      <TrainerFilePreviewModal
        trainerId={trainerId}
        fileId={previewFileId}
        fileName={files.find((f) => f.id === previewFileId)?.originalFileName}
        onCloseRequest={() => setPreviewFileId(null)}
      />

      <PaziaDeleteModal
        id="delete-trainer-file"
        isOpen={deleteFileId !== null}
        title="Usuń plik"
        description={`Czy na pewno chcesz usunąć plik „${
          files.find((f) => f.id === deleteFileId)?.originalFileName ?? ""
        }”? Tej operacji nie można cofnąć.`}
        onCloseRequest={() => setDeleteFileId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

const TrainerFileRow = ({
  trainerId,
  file,
  onDownload,
  onSetStatus,
  onPreview,
  onDelete,
}: {
  trainerId: number;
  file: TrainerFileApi;
  onDownload: () => void;
  onSetStatus: (status: TrainerFileStatusApi) => void;
  onPreview: () => void;
  onDelete: () => void;
}) => {
  const [showComments, setShowComments] = useState(false);
  const { showError } = useToaster();
  const { data: comments = [] } = useTrainerFileComments(trainerId, file.id, showComments);
  const addComment = useAddTrainerFileCommentAdmin(trainerId, file.id);
  const [draft, setDraft] = useState("");

  const submitComment = async () => {
    if (!draft.trim()) return;
    try {
      await addComment.mutateAsync(draft.trim());
      setDraft("");
    } catch {
      showError("Nie udało się dodać komentarza");
    }
  };

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{file.originalFileName}</div>
          <div className="text-xs text-slate-500">
            {file.uploadedByType === TRAINER_FILE_UPLOADED_BY.TRAINER ? "Od trenera" : "Od administracji"} ·{" "}
            {file.uploaderName} · {new Date(file.createdDate).toLocaleDateString("pl-PL")}
            {file.note ? ` · ${file.note}` : ""}
          </div>
        </div>
        {file.uploadedByType === TRAINER_FILE_UPLOADED_BY.TRAINER && file.status && (
          <select
            value={file.status}
            onChange={(e) => onSetStatus(Number(e.target.value) as TrainerFileStatusApi)}
            className="text-xs border rounded px-2 py-1"
          >
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        )}
        {PREVIEWABLE_CONTENT_TYPES.includes(file.contentType) && (
          <button onClick={onPreview} className="text-sm font-semibold text-teal-700">
            Podgląd
          </button>
        )}
        <button onClick={onDownload} className="text-sm font-semibold text-teal-700">
          Pobierz
        </button>
        {file.uploadedByType === TRAINER_FILE_UPLOADED_BY.ADMIN && (
          <button onClick={onDelete} className="text-sm font-semibold text-rose-600">
            Usuń
          </button>
        )}
      </div>

      <button
        onClick={() => setShowComments((v) => !v)}
        className="text-xs font-semibold text-slate-500 mt-2"
      >
        {showComments ? "Ukryj komentarze" : `Komentarze (${file.commentCount})`}
      </button>

      {showComments && (
        <div className="mt-2 pt-2 border-t flex flex-col gap-2">
          {comments.map((c: TrainerFileCommentApi) => (
            <div key={c.id} className="text-sm">
              <span className="font-semibold text-teal-700">{c.authorName}</span>{" "}
              <span className="text-xs text-slate-400">
                {new Date(c.createdDate).toLocaleString("pl-PL")}
              </span>
              <div>{c.body}</div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Napisz komentarz…"
              className="flex-1 border rounded px-2 py-1 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitComment();
              }}
            />
            <button onClick={submitComment} className="text-xs font-semibold text-teal-700">
              Wyślij
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
